import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Per-page SEO tags. Drop <Seo ... /> at the top of any page component.
 *
 * This component is the SINGLE SOURCE for the SEO tags that change per page:
 * <title>, description, canonical, robots, and the Open Graph / Twitter
 * title+description+url, and og:image / twitter:image (the page's own image,
 * else the logo from DefaultSeo; index.html's copies are only the pre-JS
 * default and helmet replaces them). The CONSTANT tags that are the same on
 * every page (og:type, og:site_name, twitter:card) live once in
 * public/index.html so they are never duplicated here.
 *
 * A site-wide default is rendered in the layout (see components/DefaultSeo.js),
 * so every page has clean tags; a page-level <Seo> simply overrides them
 * (react-helmet-async de-duplicates and the deepest instance wins).
 *
 * NOTE: this is a client-side SPA — tags are set after JS runs. Google renders
 * JS and picks them up. Non-JS crawlers (AI bots, link previews) get a
 * prerendered copy with these tags baked in (see prerender.js and
 * SERVER_DEPLOY_GUIDE.md).
 */

const SITE_URL = "https://bestowalsystems.com";
const SITE_NAME = "Bestowal Systems and Services Pvt. Ltd.";
// Titles are keyword-first: "<page> | Bestowal Systems". Google shows ~60
// characters, so a long page title is used on its own rather than cut off.
const TITLE_SUFFIX = " | Bestowal Systems";
const MAX_TITLE = 60;

const Seo = ({
  title,
  // Use `fullTitle` to set the exact <title> verbatim (no " | <site name>" suffix).
  fullTitle: exactTitle,
  description,
  path = "",
  image,
  noindex = false,
  // Optional per-page structured data (a schema.org object or an array of them),
  // e.g. an Article for a blog post or a Product for a product page. Emitted as
  // a JSON-LD <script>, so Google and AI crawlers can read it.
  jsonLd,
}) => {
  const fullTitle =
    exactTitle ||
    (title ? (title.length + TITLE_SUFFIX.length <= MAX_TITLE ? title + TITLE_SUFFIX : title) : SITE_NAME);
  // `path` should be a route like "/about"; falls back to the homepage.
  const canonical = `${SITE_URL}${path}`.replace(/\/+$/, "") || SITE_URL;
  // Social sites ignore relative og:image URLs (e.g. "/uploads/x.webp"), so
  // root-relative and protocol-relative images are made absolute.
  const imageUrl =
    typeof image === "string" && image.startsWith("//")
      ? `https:${image}`
      : typeof image === "string" && image.startsWith("/")
      ? `${SITE_URL}${image}`
      : image;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph (title/description/url/image vary per page; type and
          site_name are constant and live in index.html) */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}

      {/* Twitter (card is constant and lives in index.html) */}
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {/* Per-page structured data (Article / Product / etc.) */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default Seo;
